import type {GridCollection, GridNode} from '@react-types/grid';
import type {Key} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {getChildNodes, getFirstItem, getLastItem} from '../collections';
import type {MultipleSelectionStateProps} from '../../@react-stately/selection';
import {
  SelectionManager,
  useMultipleSelectionState,
} from '../../@react-stately/selection';

export interface GridState<T, C extends GridCollection<T>> {
  collection: C;
  /** A set of keys for rows that are disabled. */
  disabledKeys: Set<Key>;
  /** A selection manager to read and update row selection state. */
  selectionManager: SelectionManager;
  /** Whether keyboard navigation is disabled, such as when the arrow keys should be handled by a component within a cell. */
  isKeyboardNavigationDisabled: boolean;
}

export interface GridStateOptions<T, C extends GridCollection<T>>
  extends MultipleSelectionStateProps {
  collection: C;
  disabledKeys?: Iterable<Key>;
  focusMode?: 'row' | 'cell';
}

/**
 * Provides state management for a grid component. Handles row selection and focusing a grid cell's focusable child if applicable.
 */
export function useGridState<T extends object, C extends GridCollection<T>>(
  props: GridStateOptions<T, C>,
): GridState<T, C> {
  const {collection, focusMode} = props;
  const selectionState = useMultipleSelectionState(props);
  const disabledKeys = useMemo(
    () => (props.disabledKeys ? new Set(props.disabledKeys) : new Set<Key>()),
    [props.disabledKeys],
  );

  const setFocusedKey = selectionState.setFocusedKey;
  selectionState.setFocusedKey = (key, child) => {
    let currentKey = key;
    // If focusMode is cell and an item is focused, focus a child cell instead.
    if (focusMode === 'cell' && currentKey != null) {
      const item = collection.getItem(currentKey);
      if (item?.type === 'item') {
        const children = getChildNodes(item, collection);
        if (child === 'last') {
          currentKey = getLastItem(children)?.key || null;
        } else {
          currentKey = getFirstItem(children)?.key || null;
        }
      }
    }

    setFocusedKey(currentKey!, child);
  };

  const selectionManager = useMemo(
    () => new SelectionManager(collection, selectionState),
    [collection, selectionState],
  );

  // Reset focused key if that item is deleted from the collection.
  const cachedCollection = useRef<GridCollection<T> | null>(null);
  useEffect(() => {
    if (
      selectionState.focusedKey != null &&
      !collection.getItem(selectionState.focusedKey)
    ) {
      const node = cachedCollection.current!.getItem(
        selectionState.focusedKey,
      )!;
      const parentNode =
        node.parentKey != null &&
        (node.type === 'cell' ||
          node.type === 'rowheader' ||
          node.type === 'column')
          ? cachedCollection.current!.getItem(node.parentKey)!
          : node;
      const cachedRows = cachedCollection.current!.rows;
      const rows = collection.rows;
      const diff = cachedRows.length - rows.length;
      let index: number = Math.min(
        diff > 1
          ? Math.max(parentNode.index! - diff + 1, 0)
          : parentNode.index!,
        rows.length - 1,
      );
      let newRow: GridNode<T> | undefined;
      while (index >= 0) {
        if (
          !selectionManager.isDisabled(rows[index].key) &&
          rows[index].type !== 'headerrow'
        ) {
          newRow = rows[index];
          break;
        }
        // Find next, not disabled row.
        if (index < rows.length - 1) {
          index++;
          // Otherwise, find previous, not disabled row.
        } else {
          if (index > parentNode.index!) {
            index = parentNode.index!;
          }
          index--;
        }
      }
      if (newRow) {
        const childNodes = newRow.hasChildNodes
          ? [...getChildNodes(newRow, collection)]
          : [];
        const keyToFocus =
          newRow.hasChildNodes &&
          parentNode !== node &&
          node.index! < childNodes.length
            ? childNodes[node.index!].key
            : newRow.key;
        selectionState.setFocusedKey(keyToFocus);
      } else {
        selectionState.setFocusedKey(null!);
      }
    }
    cachedCollection.current = collection;
  }, [collection, selectionManager, selectionState, selectionState.focusedKey]);

  return {
    collection,
    disabledKeys,
    isKeyboardNavigationDisabled: false,
    selectionManager,
  };
}
